import { useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import set from 'lodash/set';
import omit from 'lodash/omit';
import { parseFromObjectToQueryString, parseFromQueryStringToObject } from 'utils/urls';

const defaultNavigateOptions = { replace: true };

/**
 * Maneja la query string en la URL.
 *
 * @IMPORTANT Hay un bug en react-router que hace el que la función setter de `useSearchParams` cambie cada vez que cambian los searchParams
 * @see https://github.com/remix-run/react-router/issues/9991
 *
 * @see https://reactrouter.com/en/main/hooks/use-search-params
 * @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
 * @see https://github.com/pbeshai/use-query-params
 * @see https://github.com/rudyhuynh/use-url-search-params
 *
 * @param {Object} arg
 * @param {Object} [arg.defaultInit] - Valor inicial para los Search Params.
 * @param {Object} [arg.parseOptions] - Opciones para parsear la Search String de la URL a objeto.
 * @param {Object} [arg.stringifyOptions] - Opciones para parsear el objeto a Search String en la URL.
 * @param {Object} [arg.navigateOptions] - Opciones para actualizar la URL... { replace: boolean, state: any, preventScrollReset: boolean }
 * @returns {Object}
 *
 * @example
 * useUrlSearchParams();
 * // => { queryParams: { foo: 1, bar: true, baz: 'hello' } }
 */
const useUrlSearchParams = ({ defaultInit, parseOptions, stringifyOptions } = {}) => {
  const [params, setParams] = useSearchParams(defaultInit);

  const parseOptionsRef = useRef(parseOptions);
  const stringifyOptionsRef = useRef(stringifyOptions);

  /**
   * Obtiene la Query String de la URL
   */
  const searchString = useMemo(() => params.toString(), [params]);

  /**
   * Obtiene los Search Params de la URL actual y los transforma a un objeto
   */
  const searchParams = useMemo(() => {
    const paramsAsObject = parseFromQueryStringToObject({
      value: searchString,
      options: parseOptionsRef.current,
    });
    return paramsAsObject;
  }, [searchString]);

  /**
   * Actualiza la URL añadiendo el Search Param indicado
   * @param {string} key
   * @param {string|number|boolean} value
   * @param {Object} [navigateOptions] - Opciones para actualizar la URL... { replace: boolean, state: any, preventScrollReset: boolean }
   */
  const setSearchParam = useCallback(
    (key, value, navigateOptions = defaultNavigateOptions) => {
      setParams(prevParams => {
        const prevParamsAsObject = parseFromQueryStringToObject({
          value: prevParams.toString(),
          options: parseOptionsRef.current,
        });
        const nextParamsAsString = parseFromObjectToQueryString({
          value: set(prevParamsAsObject, key, value),
          options: stringifyOptionsRef.current,
        });
        return nextParamsAsString;
      }, navigateOptions);
    },
    [setParams],
  );

  /**
   * Actualiza la URL con los Search Params que recibe (eliminando los existentes)
   * @param {Object} value
   * @param {Object} [navigateOptions] - Opciones para actualizar la URL... { replace: boolean, state: any, preventScrollReset: boolean }
   */
  const updateSearchParams = useCallback(
    (value, navigateOptions = defaultNavigateOptions) => {
      const paramsAsString = parseFromObjectToQueryString({
        value,
        options: stringifyOptionsRef.current,
      });
      setParams(paramsAsString, navigateOptions);
    },
    [setParams],
  );

  /**
   * Actualiza la URL manteniendo los Search Params actuales y añadiendo los que recibe
   * @param {Object} value
   * @param {Object} [navigateOptions] - Opciones para actualizar la URL... { replace: boolean, state: any, preventScrollReset: boolean }
   */
  const setSearchParams = useCallback(
    (value, navigateOptions = defaultNavigateOptions) => {
      const mergedParams = { ...searchParams, ...value };
      updateSearchParams(mergedParams, navigateOptions);
    },
    [searchParams, updateSearchParams],
  );

  /**
   * Actualiza la URL quitando el Search Param indicado
   * @param {string} key
   */
  const unsetSearchParam = useCallback(
    key => {
      const newValue = omit(searchParams, key);
      updateSearchParams(newValue);
    },
    [searchParams, updateSearchParams],
  );

  /**
   * Actualiza la URL quitando los Search Params indicados
   * @param {Array.<string>} key
   */
  const unsetSearchParams = useCallback(
    keys => {
      const newValue = omit(searchParams, keys);
      updateSearchParams(newValue);
    },
    [searchParams, updateSearchParams],
  );

  /**
   * Actualiza la URL eliminando todos los Search Params
   * @param {Object} [navigateOptions] - Opciones para actualizar la URL... { replace: boolean, state: any, preventScrollReset: boolean }
   */
  const removeSearchParams = useCallback(
    (navigateOptions = defaultNavigateOptions) => {
      setParams('', navigateOptions);
    },
    [setParams],
  );

  return {
    searchString,
    searchParams,
    setSearchParam,
    setSearchParams,
    updateSearchParams,
    unsetSearchParam,
    unsetSearchParams,
    removeSearchParams,
  };
};

export default useUrlSearchParams;
